import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Upload, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useApi, errMsg, imgUrl } from "@/lib/api";

export function useUpload() {
  const api = useApi();
  return async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/uploads", fd);
    return data;
  };
}

export function FileUpload({ kind = "attachment", onUploaded, label = "Attach files", accept, testId = "file-upload-input", multiple = true }) {
  const upload = useUpload();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    setBusy(true);
    for (const f of files) {
      try { onUploaded(await upload(f, kind)); } catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" hidden multiple={multiple} accept={accept} onChange={onChange} data-testid={testId} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-button`}
        className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : kind === "image" ? <Upload className="h-4 w-4" /> : <Paperclip className="h-4 w-4" />} {label}
      </button>
    </>
  );
}

export const FileChips = ({ files, onRemove }) => (
  <div className="flex flex-wrap gap-2">
    {files.map((f) => (
      <span key={f.id} className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1 text-xs">
        <Paperclip className="h-3 w-3" />{f.filename || f.original_filename}
        {onRemove && <button type="button" onClick={() => onRemove(f.id)} aria-label="Remove"><X className="h-3 w-3" /></button>}
      </span>
    ))}
  </div>
);

export function ImageList({ images, setImages, testId = "image-upload" }) {
  const [url, setUrl] = useState("");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {images.map((src, i) => (
          <div key={i} className="relative h-20 w-28 overflow-hidden rounded-lg border border-border">
            <img src={imgUrl(src)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded bg-background/90 p-0.5" aria-label="Remove image"><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FileUpload kind="image" accept="image/*" label="Upload image" testId={testId} onUploaded={(f) => setImages([...images, f.url])} />
        <span className="text-xs text-muted-foreground">or</span>
        <Input className="h-9 max-w-xs" placeholder="Paste image URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`${testId}-url-input`} />
        <button type="button" className="text-sm font-medium text-primary" onClick={() => { if (url.startsWith("http")) { setImages([...images, url]); setUrl(""); } }} data-testid={`${testId}-url-add`}>Add</button>
      </div>
    </div>
  );
}

export function TagInput({ value, onChange, placeholder = "Type and press Enter", testId = "tag-input" }) {
  const [t, setT] = useState("");
  const add = () => { const v = t.trim(); if (v && !value.includes(v)) onChange([...value, v]); setT(""); };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-input bg-background p-2">
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs font-medium">
          {v}<button type="button" onClick={() => onChange(value.filter((x) => x !== v))} aria-label={`Remove ${v}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
      <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add}
        placeholder={placeholder} className="min-w-[140px] flex-1 bg-transparent text-sm outline-none" data-testid={testId} />
    </div>
  );
}
